import {
  strategyApi,
  linkedInProfileApi,
  seriesFormatApi,
  postIdeaApi,
  editorialPlanApi,
  linkedInPostApi,
  publicationCalendarApi,
  scheduledPostApi,
  linkedInApi,
  StrategyResult,
} from '../services/api';
import { EMPTY_FLOW_DATA, FlowData } from './flowStatus';

/**
 * Laedt die Zaehler fuer den Prozessstatus parallel ueber die vorhandenen
 * List-Endpunkte. Jeder Aufruf ist einzeln abgesichert: ein Fehler (oder ein
 * `undefined` aus automockten Services in Tests) faellt auf den Leerwert
 * zurueck, statt den ganzen Status zu kippen.
 */
export interface LoadFlowDataResult {
  data: FlowData;
  errors: string[];
}

async function safe<T>(name: string, fn: () => Promise<T> | T | undefined, fallback: T, errors: string[]): Promise<T> {
  try {
    const value = await Promise.resolve().then(fn);
    return (value ?? fallback) as T;
  } catch (err: any) {
    errors.push(`${name}: ${err?.response?.data?.error || err?.message || 'Fehler'}`);
    return fallback;
  }
}

function parseIds(json: string | null | undefined): string[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export async function loadFlowData(): Promise<LoadFlowDataResult> {
  const errors: string[] = [];
  const [
    strategies, profiles, seriesFormats, postIdeas, editorialPlans, linkedInPosts, calendar, scheduled, linkedIn,
  ] = await Promise.all([
    safe<StrategyResult[]>('Strategien', () => strategyApi.getAll(true), [], errors),
    safe('Referenzprofile', () => linkedInProfileApi.getAll(), [], errors),
    safe('Serienformate', () => seriesFormatApi.getAll(), [], errors),
    safe('Beitragsideen', () => postIdeaApi.getAll(), [], errors),
    safe('Wochenpläne', () => editorialPlanApi.getAll(), [], errors),
    safe('Beiträge', () => linkedInPostApi.getAll(), [], errors),
    safe('Terminieren', () => publicationCalendarApi.getAll(), [], errors),
    safe('Freigabe & Versand', () => scheduledPostApi.getScheduledPosts({ limit: 200 }), { posts: [], total: 0, limit: 0, offset: 0 }, errors),
    safe('LinkedIn-Status', () => linkedInApi.getStatus(), { configured: false, connections: [] }, errors),
  ]);

  const list = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  const strategyList = list<StrategyResult>(strategies);
  const active = strategyList.find((s) => s.isActive) ?? null;
  const ideas = list<{ contentBrief?: unknown }>(postIdeas);
  const calendarEntries = list<{ status?: string }>(calendar);
  const scheduledPosts = list<{ status?: string }>((scheduled as any)?.posts);
  const connections = list<{ isActive?: boolean }>((linkedIn as any)?.connections);

  const data: FlowData = {
    ...EMPTY_FLOW_DATA,
    strategies: strategyList.length,
    activeStrategy: active
      ? { id: active.id, name: active.name, linkedProfileIds: parseIds(active.linkedInProfileIds) }
      : null,
    profiles: list(profiles).length,
    seriesFormats: list(seriesFormats).length,
    postIdeas: ideas.length,
    contentBriefs: ideas.filter((i) => Boolean(i.contentBrief)).length,
    editorialPlans: list(editorialPlans).length,
    linkedInPosts: list(linkedInPosts).length,
    calendar: {
      total: calendarEntries.length,
      scheduled: calendarEntries.filter((e) => e.status === 'scheduled' || e.status === 'approved').length,
      published: calendarEntries.filter((e) => e.status === 'published').length,
    },
    scheduledPosts: {
      pending: scheduledPosts.filter((p) => p.status === 'pending').length,
      posted: scheduledPosts.filter((p) => p.status === 'posted').length,
    },
    linkedIn: {
      configured: Boolean((linkedIn as any)?.configured),
      connected: connections.some((c) => Boolean(c.isActive)),
    },
    loadedAt: Date.now(),
  };

  return { data, errors };
}
